import { ITool, ILinkBuilder, ILinkContext } from '@/creditflowresearch/domain/vo/ITools';
import { TOOL_CONFIG } from '@/creditflowresearch/domain/Header/Tool/config';
import {
  MARKET_OVERVIEW_HYC,
  MARKET_OVERVIEW_IGC,
  CREDIT_FLOW_ARTICLES,
  NEW_ISSUE_STATS,
  IG_ACTIVE_CALENDAR,
  IG_PIPELINE,
  HYC_ACTIVE_CALENDAR,
  HYC_PIPELINE,
  CORPORATE_BOND_SCREENER,
} from '@/creditflowresearch/domain/Header/Tool/constants';
import Region from '@/creditflowresearch/domain/Region';
import { EuropeanMarket, UsMarket } from '@/creditflowresearch/domain/Market';
import CorpSegment from '@/creditflowresearch/domain/Segment/entities/CorpSegment';
import FigSegment from '@/creditflowresearch/domain/Segment/entities/FigSegment';
import SsaSegment from '@/creditflowresearch/domain/Segment/entities/SsaSegment';
import Product from '@/creditflowresearch/domain/Product';
import { ProductType } from '@/creditflowresearch/domain/Product/types/ProductType';

class LinkBuilder {
  private static isAbsProduct(productAbbreviation: string): boolean {
    return Product.isOfType(productAbbreviation, ProductType.ABS);
  }

  private static buildSegmentsForProduct(productAbbreviation: string, marketAbbreviation: string): string {
    if (marketAbbreviation === UsMarket.ABBREVIATION) {
      return [CorpSegment.TYPE, FigSegment.TYPE].toString();
    }

    return Product.isOfType(productAbbreviation, ProductType.IGC)
      ? [CorpSegment.TYPE, FigSegment.TYPE, SsaSegment.TYPE].toString()
      : [CorpSegment.TYPE, FigSegment.TYPE].toString();
  }

  private static buildMarketSegmentQuery(
    productAbbreviation: string,
    regionAbbreviation: string,
  ): string {
    const market = Region.from(regionAbbreviation).belongsToMarket();
    const segments = LinkBuilder.buildSegmentsForProduct(productAbbreviation, market.getAbbreviation());
    return `products=${ productAbbreviation }&segments=${ segments }&markets=${ market.getAbbreviation() }`;
  }

  private static buildProductMarketQuery(
    productAbbreviation: string,
    regionAbbreviation: string,
  ): string {
    const market = Region.from(regionAbbreviation).belongsToMarket();
    return `products=${ productAbbreviation }&markets=${ market.getAbbreviation() }`;
  }

  private static buildProductRegionQuery(
    productAbbreviation: string,
    regionAbbreviation: string,
  ): string {
    return `products=${ productAbbreviation }&regions=${ regionAbbreviation }`;
  }

  private static readonly PIPELINE_TOOL_IDS = new Set([IG_PIPELINE, HYC_PIPELINE]);

  private static readonly CORPORATE_AC_TOOL_IDS = new Set([IG_ACTIVE_CALENDAR, HYC_ACTIVE_CALENDAR]);

  private static readonly BUILDERS: ILinkBuilder[] = [
    {
      canBuild: (tool) => !!tool.outOfDomain,
      build: (tool) => tool.path || '',
    },
    {
      canBuild: (tool) => !!tool.external && !tool.outOfDomain,
      build: (tool) => {
        const hostname = TOOL_CONFIG.externalHosts[tool.id];
        return hostname
          ? `${ TOOL_CONFIG.domainProtocol }//${ hostname }/${ tool.route }`
          : '';
      },
    },
    {
      canBuild: (tool, context) => {
        if (tool.id === MARKET_OVERVIEW_IGC || tool.id === IG_ACTIVE_CALENDAR) {
          return true;
        }
        if (tool.id === CREDIT_FLOW_ARTICLES && context) {
          return !LinkBuilder.isAbsProduct(context.activeProductAbbreviation);
        }
        return false;
      },
      build: (tool, context) => {
        const pathname = `/${ tool.route }`;
        const search = LinkBuilder.buildMarketSegmentQuery(
          context.activeProductAbbreviation,
          context.activeRegionAbbreviation,
        );
        return `${ pathname }?${ search }`;
      },
    },
    {
      canBuild: (tool) => tool.id === MARKET_OVERVIEW_HYC,
      build: (tool, context) => {
        const market = Region.from(context.activeRegionAbbreviation).belongsToMarket();
        const pathname = `/${ tool.route }`;
        const segments = LinkBuilder.buildSegmentsForProduct(context.activeProductAbbreviation, market.getAbbreviation());
        const search = `products=${ context.activeProductAbbreviation }&segments=${ segments }&markets=${ market.getAbbreviation() }`;
        return `${ pathname }?${ search }`;
      },
    },
    {
      canBuild: (tool, context) => {
        if (tool.id === CREDIT_FLOW_ARTICLES && context) {
          return LinkBuilder.isAbsProduct(context.activeProductAbbreviation);
        }
        return false;
      },
      build: (tool, context) => {
        const pathname = `/${ tool.route }`;
        const search = LinkBuilder.buildProductRegionQuery(
          context.activeProductAbbreviation,
          context.activeRegionAbbreviation,
        );
        return `${ pathname }?${ search }`;
      },
    },
    {
      canBuild: (tool, context) =>
        tool.id === NEW_ISSUE_STATS && context && !LinkBuilder.isAbsProduct(context.activeProductAbbreviation),
      build: (tool, context) => {
        const pathname = `/${ tool.route }`;
        const search = LinkBuilder.buildProductMarketQuery(
          context.activeProductAbbreviation,
          context.activeRegionAbbreviation,
        );
        return `${ pathname }?${ search }`;
      },
    },
    {
      canBuild: (tool) => LinkBuilder.PIPELINE_TOOL_IDS.has(tool.id),
      build: (tool, context) => {
        const segments = LinkBuilder.buildSegmentsForProduct(context.activeProductAbbreviation, EuropeanMarket.ABBREVIATION);
        return `/${ tool.route }?products=${ context.activeProductAbbreviation }&markets=${ EuropeanMarket.ABBREVIATION }&segments=${ segments }`;
      },
    },
    {
      canBuild: (tool) => LinkBuilder.CORPORATE_AC_TOOL_IDS.has(tool.id),
      build: (tool, context) => {
        const market = Region.from(context.activeRegionAbbreviation).belongsToMarket();
        const segments = LinkBuilder.buildSegmentsForProduct(context.activeProductAbbreviation, market.getAbbreviation());
        return `/${ tool.route }?products=${ context.activeProductAbbreviation }&markets=${ market.getAbbreviation() }&segments=${ segments }`;
      },
    },
    {
      canBuild: (tool) => tool.id === CORPORATE_BOND_SCREENER,
      build: (tool, context) => {
        const market = Region.from(context.activeRegionAbbreviation).belongsToMarket();
        const segments = [CorpSegment.TYPE, FigSegment.TYPE, SsaSegment.TYPE].toString();
        const pathname = `/${ tool.route }`;
        const search = `products=${ context.activeProductAbbreviation }&markets=${ market.getAbbreviation() }&segments=${ segments }`;
        return `${ pathname }?${ search }`;
      },
    },
    {
      canBuild: (tool) => !tool.external && !tool.outOfDomain,
      build: (tool, context) => {
        const pathname = `/${ tool.route }`;
        const search = tool.skipProductRegionAppend
          ? ''
          : LinkBuilder.buildProductRegionQuery(
            context.activeProductAbbreviation,
            context.activeRegionAbbreviation,
          );

        return search ? `${ pathname }?${ search }` : pathname;
      },
    },
  ];

  static buildLink(tool: ITool, context: ILinkContext): string {
    const builder = LinkBuilder.BUILDERS.find((b) => b.canBuild(tool, context));
    return builder ? builder.build(tool, context) : '';
  }

  static registerBuilder(builder: ILinkBuilder): void {
    LinkBuilder.BUILDERS.push(builder);
  }

  static buildExternalLink(toolId: string, route: string): string | null {
    const hostname = TOOL_CONFIG.externalHosts[toolId];
    return hostname
      ? `${ TOOL_CONFIG.domainProtocol }//${ hostname }/${ route }`
      : null;
  }

  private static readonly INTERNAL_LINK_BUILDERS: Array<{
    canBuild: (toolId?: string, skipProductRegionAppend?: boolean | string, activeProductAbbreviation?: string) => boolean,
    build: (
      route: string,
      activeProductAbbreviation: string,
      activeRegionAbbreviation: string,
    ) => { pathname: string, search: string },
  }> = [
    {
      canBuild: (toolId, skipProductRegionAppend) => !!skipProductRegionAppend,
      build: (route) => ({ pathname: `/${ route }`, search: '' }),
    },
    {
      canBuild: (toolId, skipProductRegionAppend, activeProductAbbreviation) => {
        if (toolId === MARKET_OVERVIEW_IGC || toolId === IG_ACTIVE_CALENDAR) {
          return true;
        }
        if (toolId === CREDIT_FLOW_ARTICLES && activeProductAbbreviation) {
          return !LinkBuilder.isAbsProduct(activeProductAbbreviation);
        }
        return false;
      },
      build: (route, activeProductAbbreviation, activeRegionAbbreviation) => ({
        pathname: `/${ route }`,
        search: LinkBuilder.buildMarketSegmentQuery(activeProductAbbreviation, activeRegionAbbreviation),
      }),
    },
    {
      canBuild: (toolId) => toolId === MARKET_OVERVIEW_HYC,
      build: (route, activeProductAbbreviation, activeRegionAbbreviation) => {
        const market = Region.from(activeRegionAbbreviation).belongsToMarket();
        const segments = LinkBuilder.buildSegmentsForProduct(activeProductAbbreviation, market.getAbbreviation());
        return {
          pathname: `/${ route }`,
          search: `products=${ activeProductAbbreviation }&segments=${ segments }&markets=${ market.getAbbreviation() }`,
        };
      },
    },
    {
      canBuild: (toolId, skipProductRegionAppend, activeProductAbbreviation) => {
        if (toolId === CREDIT_FLOW_ARTICLES && activeProductAbbreviation) {
          return LinkBuilder.isAbsProduct(activeProductAbbreviation);
        }
        return false;
      },
      build: (route, activeProductAbbreviation, activeRegionAbbreviation) => ({
        pathname: `/${ route }`,
        search: LinkBuilder.buildProductRegionQuery(activeProductAbbreviation, activeRegionAbbreviation),
      }),
    },
    {
      canBuild: (toolId, skipProductRegionAppend, activeProductAbbreviation) =>
        toolId === NEW_ISSUE_STATS && !!activeProductAbbreviation && !LinkBuilder.isAbsProduct(activeProductAbbreviation),
      build: (route, activeProductAbbreviation, activeRegionAbbreviation) => ({
        pathname: `/${ route }`,
        search: LinkBuilder.buildProductMarketQuery(activeProductAbbreviation, activeRegionAbbreviation),
      }),
    },
    {
      canBuild: (toolId) => LinkBuilder.PIPELINE_TOOL_IDS.has(toolId),
      build: (route, activeProductAbbreviation) => {
        const segments = LinkBuilder.buildSegmentsForProduct(activeProductAbbreviation, EuropeanMarket.ABBREVIATION);
        return {
          pathname: `/${ route }`,
          search: `products=${ activeProductAbbreviation }&markets=${ EuropeanMarket.ABBREVIATION }&segments=${ segments }`,
        };
      },
    },
    {
      canBuild: (toolId) => LinkBuilder.CORPORATE_AC_TOOL_IDS.has(toolId),
      build: (route, activeProductAbbreviation, activeRegionAbbreviation) => {
        const market = Region.from(activeRegionAbbreviation).belongsToMarket();
        const segments = LinkBuilder.buildSegmentsForProduct(activeProductAbbreviation, market.getAbbreviation());
        return {
          pathname: `/${ route }`,
          search: `products=${ activeProductAbbreviation }&markets=${ market.getAbbreviation() }&segments=${ segments }`,
        };
      },
    },
    {
      canBuild: (toolId) => toolId === CORPORATE_BOND_SCREENER,
      build: (route, activeProductAbbreviation, activeRegionAbbreviation) => {
        const market = Region.from(activeRegionAbbreviation).belongsToMarket();
        const segments = [CorpSegment.TYPE, FigSegment.TYPE, SsaSegment.TYPE].toString();
        return {
          pathname: `/${ route }`,
          search: `products=${ activeProductAbbreviation }&markets=${ market.getAbbreviation() }&segments=${ segments }`,
        };
      },
    },
    {
      canBuild: () => true,
      build: (route, activeProductAbbreviation, activeRegionAbbreviation) => ({
        pathname: `/${ route }`,
        search: LinkBuilder.buildProductRegionQuery(activeProductAbbreviation, activeRegionAbbreviation),
      }),
    },
  ];

  static buildInternalLink(
    route: string,
    activeProductAbbreviation: string,
    activeRegionAbbreviation: string,
    skipProductRegionAppend?: boolean | string,
    toolId?: string,
  ): { pathname: string, search: string } {
    const builder = LinkBuilder.INTERNAL_LINK_BUILDERS.find((b) =>
      b.canBuild(toolId, skipProductRegionAppend, activeProductAbbreviation),
    );

    return builder
      ? builder.build(route, activeProductAbbreviation, activeRegionAbbreviation)
      : { pathname: `/${ route }`, search: '' };
  }
}

export default LinkBuilder;
